import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '../../components/nav';
import { SourceBadge } from '../../components/SourceBadge';
import { CONFIDENCE_LABEL, STATUS_LABEL, type Activity, type ActivityItem, type Passage } from '../../data/types';
import { useAppData } from '../../state/AppData';
import { useSelection } from '../../state/selection';
import { shuffle, writeItem, type ItemState } from './progress';
import { chapterRoman } from '../reader/segments';

const STATUS_ORDER = ['preserved', 'transformed', 'lost', 'undetermined'];

/** Un reto no se corrige hasta que hay hipótesis; la explicación y la evidencia llegan después (spec §10.4). */
export function ActivityItemView({ activity, item, onResolved }: { activity: Activity; item: ActivityItem; onResolved: (n: number, s: ItemState) => void }) {
  const [answer, setAnswer] = useState<string | null>(null);
  const [order, setOrder] = useState<Passage[]>(() => shuffle(item.entries ?? [], activity.id + item.n));
  const [checked, setChecked] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [hintShown, setHintShown] = useState(false);
  const sel = useSelection();
  const { mentionById, chapterCollection, cities, city } = useAppData();

  const options = useMemo(() => shuffle(item.options ?? [], activity.id + item.n), [activity.id, item]);
  const correctValue = item.answerPlaceId ?? item.answerKey ?? item.answerStatus ?? null;
  const orderOk = order.every((e, i) => e.position === i + 1);
  const isRight = item.entries ? orderOk : answer === correctValue;
  const uncertain = item.status ? item.status.confidence === 'uncertain' || item.status.value === 'undetermined' : false;
  const mention = item.evidence ? mentionById.get(item.evidence.mentionId) : undefined;
  // Un reto puede cruzar colecciones (act-obra-*): entonces el pasaje no está en el ámbito activo y
  // no hay mención cargada. El enlace lleva igualmente al pasaje, cambiando de ámbito y diciéndolo.
  const away = useMemo(() => {
    const par = item.evidence?.paragraphId ?? item.passage?.paragraphId;
    if (!par || mention) return null;
    const col = chapterCollection.get(par.slice(0, 7));
    if (!col || col === city.collectionId) return null;
    const scope = cities.find((c) => c.collectionId === col);
    return scope ? { to: `/leer/${par.slice(0, 7)}?ciudad=${scope.slug}&paragraph=${par}`, scope } : null;
  }, [item, mention, chapterCollection, cities, city]);

  const check = () => {
    setChecked(true);
    onResolved(item.n, (item.entries ? orderOk : answer === correctValue) ? 'resuelto' : 'revelado');
  };
  const reveal = () => {
    setChecked(true);
    setGaveUp(true);
    onResolved(item.n, 'revelado');
  };
  const retry = () => { setChecked(false); setGaveUp(false); };
  const move = (i: number, d: -1 | 1) => {
    setOrder((prev) => {
      const next = [...prev];
      const j = i + d;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  return (
    <section className="reto" aria-labelledby={`item-${item.n}`}>
      <h3 id={`item-${item.n}`}>{item.prompt}</h3>

      {item.passage && <Quote passage={item.passage} />}

      {item.entries && (
        <ol className="reto__order">
          {order.map((e, i) => (
            <li key={e.placeId + e.paragraphId}>
              <span className="reto__order-name">{e.placeName}</span>
              {checked && <span className="reto__order-page"> · p. {e.printedPage ?? '—'} · cap. {chapterRoman(e.paragraphId)}</span>}
              <span className="reto__order-btns">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || checked} aria-label={`Subir ${e.placeName}`}>↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === order.length - 1 || checked} aria-label={`Bajar ${e.placeName}`}>↓</button>
              </span>
            </li>
          ))}
        </ol>
      )}

      {item.options && (
        <fieldset className="reto__options" disabled={checked}>
          <legend className="visually-hidden">Opciones</legend>
          {options.map((o) => {
            const value = o.key ?? o.placeId ?? '';
            return (
              <label key={value} className="reto__option">
                <input type="radio" name={`${activity.id}-${item.n}`} value={value} checked={answer === value} onChange={() => setAnswer(value)} />
                {o.quote ? <span className="reto__option-quote">«{o.quote}»<small> — {o.placeName}, p. {o.printedPage ?? '—'}</small></span> : <span>{o.placeName}</span>}
              </label>
            );
          })}
        </fieldset>
      )}

      {item.answerStatus && (
        <fieldset className="reto__options" disabled={checked}>
          <legend className="visually-hidden">Estado propuesto</legend>
          {STATUS_ORDER.map((s) => (
            <label key={s} className="reto__option">
              <input type="radio" name={`${activity.id}-${item.n}`} value={s} checked={answer === s} onChange={() => setAnswer(s)} />
              <span>{STATUS_LABEL[s]}</span>
            </label>
          ))}
        </fieldset>
      )}

      {hintShown && item.hint && <p className="reto__hint"><strong>Pista.</strong> {item.hint}</p>}

      <p className="reto__actions">
        {!checked && <button type="button" className="btn" onClick={check} disabled={!item.entries && !answer}>Comprobar</button>}
        {!checked && item.hint && !hintShown && <button type="button" className="link-button" onClick={() => setHintShown(true)}>Ver una pista</button>}
        {!checked && <button type="button" className="link-button" onClick={reveal}>No lo sé</button>}
        {checked && <button type="button" className="link-button" onClick={retry}>Revisar mi hipótesis</button>}
      </p>

      {checked && (
        <div className={`reto__feedback reto__feedback--${gaveUp ? 'reveal' : isRight ? 'ok' : uncertain ? 'reveal' : 'no'}`} role="status">
          <p className="reto__verdict">
            {gaveUp ? 'Sin respuesta: esto es lo que dicen las fuentes.'
              : isRight ? 'Coincide con lo que sostienen las fuentes.'
                : uncertain ? 'Tu propuesta es defendible, pero las fuentes disponibles dicen otra cosa.'
                  : 'No coincide con la evidencia.'}
          </p>

          {item.entries && !orderOk && !gaveUp && <p>Este es el orden que sostienen el texto y el mapa:</p>}
          {item.entries && (
            <ol className="reto__solution">
              {[...(item.entries ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map((e) => (
                <li key={e.placeId + e.paragraphId}>{e.placeName} <small>p. {e.printedPage ?? '—'}</small></li>
              ))}
            </ol>
          )}

          {item.answerPlaceId && (
            <p>Es <strong>{(item.options ?? []).find((o) => o.placeId === item.answerPlaceId)?.placeName}</strong>.</p>
          )}

          {item.answerKey && (
            <blockquote className="reto__solution-quote">
              <p>«{(item.options ?? []).find((o) => o.key === item.answerKey)?.quote}»</p>
            </blockquote>
          )}

          {item.status && (
            <div className="reto__status">
              <p><strong>{STATUS_LABEL[item.status.value]}</strong> · certeza {CONFIDENCE_LABEL[item.status.confidence] ?? item.status.confidence}</p>
              <p>
                <SourceBadge kind={badgeKind(item.status.sourceId)} detail={item.status.sourceLocator ?? undefined} />
                {item.status.note ? ` ${item.status.note}` : ''}
              </p>
              {item.status.value === 'undetermined' && <p>No damos por cierto lo que ninguna fuente sostiene: queda abierto.</p>}
            </div>
          )}

          {item.explanation && <p>{item.explanation}</p>}

          <p className="reto__evidence">
            {mention && <button type="button" className="link-button" onClick={() => sel.readInContext(mention)}>Leer el pasaje en el libro</button>}
            {away && <RouterLink to={away.to}>Leer el pasaje en {away.scope.section}</RouterLink>}
            {item.passage && !away && <> · <Link to={`/lugares/${item.passage.placeSlug}`}>Ficha del lugar</Link></>}
          </p>
        </div>
      )}
    </section>
  );
}

function badgeKind(sourceId?: string | null) {
  if (sourceId === 'jcyl_relacion_monumentos') return 'jcyl' as const;
  if (sourceId === 'osm_overpass_2026_09_17') return 'osm' as const;
  if (sourceId === 'wikidata') return 'wikidata' as const;
  if (sourceId === 'ia_quadrado_sav_1884') return 'quadrado' as const;
  return 'editorial' as const;
}

function Quote({ passage }: { passage: Passage }) {
  return (
    <blockquote className="reto__quote">
      <p>«{passage.quote}»</p>
      <footer><SourceBadge kind="quadrado" detail={`p. ${passage.printedPage ?? '—'} · cap. ${chapterRoman(passage.paragraphId)}`} /></footer>
    </blockquote>
  );
}

export { writeItem };
